import { Group, Select, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { type SchemaListFilters } from '../../../types';
import { schemaStatuses, type SchemaRegistry } from '@morph-mapper/types';
import { StyledFilterSelect, StyledSearchInput } from './styles';

type SchemaListFiltersBarProps = {
  filters: SchemaListFilters;
  onFiltersChange: (filters: SchemaListFilters) => void;
  registry?: SchemaRegistry;
};

const SEARCH_DEBOUNCE_MS = 300;

export const SchemaListFiltersBar = ({
  filters,
  onFiltersChange,
  registry,
}: SchemaListFiltersBarProps) => {
  const [searchDraft, setSearchDraft] = useState(filters.search ?? '');
  const [debouncedSearch] = useDebouncedValue(searchDraft, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    setSearchDraft(filters.search ?? '');
  }, [filters.search]);

  useEffect(() => {
    if (debouncedSearch === (filters.search ?? '')) {
      return;
    }

    onFiltersChange({
      ...filters,
      search: debouncedSearch || undefined,
    });
  }, [debouncedSearch, filters, onFiltersChange]);

  return (
    <Group spacing="sm">
      <StyledSearchInput>
        <TextInput
          placeholder="Search schemas..."
          value={searchDraft}
          size="sm"
          onChange={(event) => setSearchDraft(event.currentTarget.value)}
        />
      </StyledSearchInput>
      <StyledFilterSelect>
        <Select
          placeholder="Domain"
          clearable
          size="sm"
          data={(registry?.schemaDomains ?? []).map((value) => ({
            label: value,
            value,
          }))}
          value={filters.domain ?? null}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              search: searchDraft || undefined,
              domain: value ?? undefined,
            })
          }
        />
      </StyledFilterSelect>
      <StyledFilterSelect>
        <Select
          placeholder="Type"
          clearable
          size="sm"
          data={(registry?.schemaTypes ?? []).map((value) => ({
            label: value,
            value,
          }))}
          value={filters.type ?? null}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              search: searchDraft || undefined,
              type: value ?? undefined,
            })
          }
        />
      </StyledFilterSelect>
      <StyledFilterSelect>
        <Select
          placeholder="Variant"
          clearable
          size="sm"
          data={(registry?.schemaVariants ?? []).map((value) => ({
            label: value,
            value,
          }))}
          value={filters.variant ?? null}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              search: searchDraft || undefined,
              variant: value ?? undefined,
            })
          }
        />
      </StyledFilterSelect>
      <StyledFilterSelect>
        <Select
          placeholder="Status"
          clearable
          size="sm"
          data={schemaStatuses.map((value) => ({
            label: value,
            value,
          }))}
          value={filters.status ?? null}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              search: searchDraft || undefined,
              status: (value as SchemaListFilters['status']) ?? undefined,
            })
          }
        />
      </StyledFilterSelect>
    </Group>
  );
};
